import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Trans } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog';
import { Footer, FooterButtons } from '@/design_system/Footer';
import { useBrands } from '@/models/brand';
import { useCreateDraftRequestArticle } from '@/models/request';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';

import { BrandInputs } from './BrandInputs';

export const BrandModal = ({
  isOpen,
  setIsOpen,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}) => {
  const navigate = useNavigate();

  const { request } = useRequestContext();

  const { data: { brands } = { brands: [] } } = useBrands({
    organizationId: request.organizationId,
    limit: 500,
    offset: 0,
  });

  const {
    mutateAsync: createArticle,
    isPending: isPendingCreateArticle,
    isSuccess: isSuccessCreateArticle,
    reset: resetCreateArticle,
  } = useCreateDraftRequestArticle(request.id);

  const [brandId, setBrandId] = useState<string | undefined>(undefined);
  const [otherBrandName, setOtherBrandName] = useState<string | undefined>(undefined);

  const [showErrors, setShowErrors] = useState<boolean>(false);

  const handleCreateArticle = async () => {
    const brand = brands.find((brand) => brand.id === brandId);

    const shouldShowErrors = !brandId || (brand?.isOtherBrand && !otherBrandName);

    if (shouldShowErrors) {
      setShowErrors(true);

      return;
    }

    if (brandId) {
      const { id } = await createArticle({
        brandId,
        otherBrandName,
      });

      handleCloseModal();

      navigate(`/requests/new/${request.id}/articles/${id}`, {
        state: { from: 'request', isNew: true, skipRouterBlocker: true },
        replace: true,
      });

      resetCreateArticle();
    }
  };

  const handleCloseModal = () => {
    setIsOpen(false);
    setBrandId(undefined);
    setOtherBrandName(undefined);
    setShowErrors(false);
  };

  return (
    <Dialog
      isOpen={isOpen}
      onOpenChange={handleCloseModal}
      title={<Trans id="brand-modal.add-item.title">Add an item</Trans>}
    >
      <main>
        <BrandInputs
          brandOptions={brands}
          selectedBrand={brands.find((brand) => brand.id === brandId)}
          onBrandSelect={(brand) => {
            setBrandId(brand?.id);
            setOtherBrandName(undefined);
          }}
          otherBrandName={otherBrandName}
          onOtherBrandChange={setOtherBrandName}
          showErrors={showErrors}
        />
      </main>

      <Footer>
        <FooterButtons>
          <Button
            variant="primary"
            size="medium"
            onPress={handleCreateArticle}
            isLoading={isPendingCreateArticle || isSuccessCreateArticle}
            disabled={isPendingCreateArticle || isSuccessCreateArticle}
          >
            <Trans id="brand-modal.add-item.button">Add an item</Trans>
          </Button>
        </FooterButtons>
      </Footer>
    </Dialog>
  );
};
